import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  RadioButtonUnchecked, // Not Proficient
  RadioButtonChecked, // Proficient
  Tonality, // Half Proficient
} from "@mui/icons-material";
import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Character,
  Skill,
  RollPlus,
  CharacterSheetInterface,
  RolledValue
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBox";
import Roller from "@dndComponents/other/Roller";
import LoadingBox from "@components/input/LoadingBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  skill: Skill;
}

const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterSkillInput({
  character,
  char_interface,
  skill,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [bonuses_string, setBonusesString] = useState("");
  const [proficiency_multiplier, setProficiencyMultiplier] = useState(0);
  const [rolls, setRolls] = useState<RollPlus[]>([]);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [open, setOpen] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    setTimeout(() => {
      const { char_roll, skill_rolls: new_rolls, bonuses_string: new_bonuses_string } = char_util.skillRolls(char_interface, skill);
      setBonusesString(new_bonuses_string);
      if (char_roll) {
        setProficiencyMultiplier(char_roll.proficiency);
      }
      setRolls(new_rolls);
    }, 100);
  }, [char_interface, skill]);

  useEffect(() => {
    character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    load();
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
      load();
    }
  }, [reloading, load]);

  const recalculated = () => {
    // console.log("Proficiencies");
    setReloading(true);
  }
  
  const renderExtras = () => {
    return (
      <>
        <Popover
          open={ popoverAnchorEl !== null }
          anchorEl={popoverAnchorEl}
          onClose={() => {
            setPopoverAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          { popoverAnchorEl !== null && renderRoll() }
        </Popover>
        <ModalOrDrawer 
          open={open} 
          onClose={() => { 
            setOpen(false); 
            character.subscription_holder.modalClosed();
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              className="header centered"
              size={{
                xs: 12
              }}>{ skill.name }</Grid>
            <Grid
              size={{
                xs: 12
              }}><DisplayStringWithReplacements display_string={ skill.description } /></Grid>
          </Grid>
        </ModalOrDrawer>
      </>
    );
  }

  const renderRoll = () => {
    return (
      <Roller 
        name={skill.name}
        rolled_values={roll}
        type="Skill Check" 
      />
    );
  }

  const minimum_roll = char_util.getMinimumRoll(char_interface, "Ability Check", skill);
  let use_ability_score = skill.use_ability_score;
  const override_feature = char_interface.skill_ability_overrides.find(o => o.skill_ids.includes(skill._id));
  if (override_feature) {
    use_ability_score = override_feature.ability_score;
  }
  const { advantage, advantages, greater_advantage } = char_util.getAdvantage(char_interface, "Ability Check", skill);
  return (
    <>
      <div className="curved_border" style={{
          padding: "4px"
        }}>
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 1
            }}>
            <div style={{ 
              float: "left",
              lineHeight: "0.5"
            }}>
              { proficiency_multiplier >= 1 ? <RadioButtonChecked /> : (proficiency_multiplier === 0.5 ? <Tonality /> : <RadioButtonUnchecked />) }
            </div>
          </Grid>
          <Grid
            size={{
              xs: 1
            }}>
            <div style={{ 
              float: "left", 
              lineHeight: "1.6" 
            }}>
              { proficiency_multiplier === 2 ? "x2" : "" }
            </div>
          </Grid>
          <Grid
            container
            spacing={0}
            direction="column"
            className="clickable"
            onClick={() => {
              setOpen(true);
            }}
            size={{
              xs: 7
            }}>
            <Grid>
              <div style={{ float: "left", lineHeight: "1.6" }}>
                { skill.name } <span style={{ color: "gray", fontSize: "11px" }}>{ use_ability_score }</span>
              </div>
            </Grid>
            { minimum_roll > 0 && 
              <Grid style={{ color: "gray", fontSize: "11px" }}>
                Minimum { minimum_roll }
              </Grid>
            }
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <DandDButtonBox 
              name={bonuses_string}
              advantage={ advantage }
              advantages={ advantages }
              greater_advantage={ greater_advantage }
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                // const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Ability Check")) && use_ability_score !== "None" && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(use_ability_score))).length > 0;
                const new_rolls = char_util.rollD20({ char_interface: character, rolls, type: "Ability Check",  use_ability_score, advantage, greater_advantage, minimum_roll });
                console.log(new_rolls);
                setRoll(new_rolls);
                setPopoverAnchorEl(event.currentTarget);
              }} 
            />
          </Grid>
        </Grid>
      </div>
      { renderExtras() }
    </>
  );
}
